import { assertUncomputedFromBase, fromBaseRecord, FromBaseInputMissing, FromBaseStageMissing } from '../janus-engine/compile/from-base-context';
import type { FromBaseRow } from '../janus-engine/compile/from-base-context';
import { entityRuntimeMetadata } from '../janus-engine/compile/entity-runtime-metadata';
import type { FromBaseEntryMetadata } from '../janus-engine/compile/from-base-entry';
import { FROM_BASE_STATIC_REGISTRIES } from '../janus-engine/compile/from-base-catalog-normalization';
import { createNpcDefinitionFinalizer } from '../janus-engine/compile/from-base-npc-finalization';
import { fromBaseCatalogFinalizationGaps } from '../janus-engine/compile/from-base-definition-finalization';
import { enchantmentPartEligible } from '../janus-engine/authoring/enchantment-part-eligibility';
import { inventoryCatalogScope, mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import type { InventorySharedCatalog } from '../janus-engine/compile/inventory-shared-catalog';
import { itemsCol, layerOf, layerHas, layerEntriesOf, layerListEntryUuid, scalarLayerSlot, primaryPictureOf, sourceOf, plTotal } from '../shared/array-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { escapeHtml } from '../shared/cosmetic-card';
import { makeNumberPretty } from '../object-card/helpers';
import { esc, iconPh } from '../object-card/card-shell';
import { inventoryListEntry } from '../janus-engine/actions/ability-preconditions';
import { plCostContract, plCatalogs, statusQuantityInfo, checkUuid } from '../janus-engine/pl/contract';
import { getUndispellableStatuses } from '../janus-engine/port/k3-rules/StatusEffect';
import { ARC_MIN, ARC_MAX, baseSweetSpotWidth, resolveDiceCount, feelFalloffForTier, struggleSecondsForTier } from '../janus-engine/minigames/lockpick';
import { compileSurfacePictures } from '../janus-engine/compile/from-base-encounter-media';
import { readLayerScoringToken } from '../janus-engine/compile/layer-cell-readers';
import { phpJsonTruthy } from '../janus-engine/compile/php-json-values';
import { phpRound } from '../janus-engine/pl/php-round';
import { definitionNameCell, definitionPlCell, definitionRow } from './definition-cells';
import type { CellSegment, TableCell, TableColumn, TableRow } from './types';

export const DEFINITION_FAMILY_SCHEMA = 'raw-definition-family-v1';
export type DefinitionField = { from: 'item' | 'layer' | 'source'; key: string };
/** Literal selectors/layout only. No expression evaluator or family-specific compiler. */
export type DefinitionCellSpec =
    | { kind: 'name'; rune?: boolean; target?: string; open_info?: boolean }
    | { kind: 'pl' }
    | { kind: 'spell'; cell: string; menta?: boolean }
    | { kind: 'enchantment'; cell: string }
    | { kind: 'media'; pixels?: number; link?: boolean; searchable?: boolean; thumbnail?: boolean; icon_only?: boolean }
    | { kind: 'scalar'; field: DefinitionField; format?: 'text' | 'number' | 'boolean'; icon?: string;
        labels?: Record<string, string>; empty?: string; true_text?: string; presence?: boolean; nonzero?: boolean;
        pretty?: boolean; zero_icon?: string; searchable?: boolean; label?: string; sort?: 'label' | 'number' | 'first-number'; default?: string | number;
        entries?: boolean; badge?: string; singularize_when?: DefinitionField }
    | { kind: 'references'; field: DefinitionField; display?: 'icons' | 'names' | 'both'; item_type?: string; separator?: string;
        sort?: 'value' | 'label' | 'id' | 'count'; searchable?: boolean; strip?: string;
        badge?: 'dark' | 'warning'; open_info?: boolean; prefix_icon?: string; label?: string;
        single?: boolean; reverse?: boolean; limit?: number; order?: 'name'; quantity?: boolean; round_quantity?: boolean; nested_types?: 'abilities' }
    | { kind: 'damage'; dice: DefinitionField; die: DefinitionField; versatile_dice: DefinitionField; versatile_die: DefinitionField;
        mod: DefinitionField; damage_type: DefinitionField; nonzero_mod?: boolean }
    | { kind: 'range'; begin: DefinitionField; end: DefinitionField }
    | { kind: 'source'; fallback?: 'na'; label?: 'name' | 'abbreviation'; empty?: string; empty_abbreviation?: string }
    | { kind: 'related'; table: string; owner_field: string; display: 'icons'; icon_field: string; link_field: string }
    | { kind: 'compound'; parts: DefinitionCellSpec[]; separator?: string; require_all?: boolean; sort?: 'first-number' | 'text' }
    | { kind: 'related-count'; table: string; owner_field: string }
    | { kind: 'spell-tally'; reference: 'circle' | 'sphere'; level?: number | 'all' }
    | { kind: 'menta-tally'; level: number | 'all' }
    | { kind: 'icon'; field: DefinitionField; icon?: string; item_type?: string; link?: boolean }
    | { kind: 'action'; action: 'roll' | 'audio'; field: DefinitionField; icon: string; variant?: string }
    | { kind: 'parsetext'; field: DefinitionField }
    | { kind: 'recipe'; part: 'skill_tier' | 'check' | 'time' | 'fabricate' }
    | { kind: 'status'; part: 'type' | 'undispellable' }
    | { kind: 'defense'; id: string }
    | { kind: 'lock'; stat: 'sweet_spot' | 'feel' | 'strain'; tier: 0 | 6 };
export type DefinitionOrder =
    | { field: DefinitionField; mode: 'text' | 'number' | 'shield-last'; missing?: 'first' | 'last' }
    | { mode: 'average-damage'; dice: DefinitionField; die: DefinitionField; mod: DefinitionField };
interface DefinitionFamilyOptions {
    saved_definitions?: true;
    finalize_definitions?: true;
    related?: string[];
    filters?: Record<string, DefinitionFilterSpec>;
    order?: DefinitionOrder[];
    spell_tally?: 'circle' | 'sphere';
    menta_tally?: true;
}
export type DefinitionFilterSpec =
    | { mode: 'source' }
    | { mode: 'number'; field: DefinitionField }
    | { mode: 'references'; field: DefinitionField; fields?: never }
    | { mode: 'references'; fields: DefinitionField[]; field?: never }
    | { mode: 'boolean'; field: DefinitionField; presence?: boolean }
    | { mode: 'flags'; parts: { field: DefinitionField; value: string }[] };
export interface DefinitionFamilyColumn extends TableColumn { project: DefinitionCellSpec; filter_projects?: Record<string, DefinitionCellSpec> }
export interface DefinitionFamilyGraph {
    schema: typeof DEFINITION_FAMILY_SCHEMA;
    family: string;
    item_type: string;
    definition_ids: string[];
    columns: DefinitionFamilyColumn[];
    base: FromBaseRow;
    shared?: InventorySharedCatalog;
    options?: DefinitionFamilyOptions;
}
export interface DefinitionProjectionContext {
    catalog: Record<string, unknown>; base: FromBaseRow; cdnUrl?: string; shieldArmorType?: string; spell?: TableRow;
    abilityTypes?: readonly string[]; inverseReferences?: Map<string, FromBaseRow[]>;
    spellTallies?: Map<string, number[]>;
    mentaTallies?: Map<string, number[]>;
}

const plain = (value: unknown): string => decodeHtmlEntitiesForPlainText(value);
const name = (row: unknown): string => plain(itemsCol(row, 'item_name'));
const link = (id: string): string => `/item.php?id=${encodeURIComponent(id)}`;
const text = (value: string): CellSegment => ({ kind: 'text', value });

/** The query order defines the first saved value of each spell layer. Keep zero
 * distinct from absence, then read the saved slots with the normal page reader. */
export function catalogSpellTallies(input: unknown): Map<string, number[]> {
    if (!Array.isArray(input)) throw new Error('catalog-spell-tally-rows');
    const spells = new Map<string, Record<string, unknown>>();
    for (const row of input) {
        if (!fromBaseRecord(row)) throw new Error('catalog-spell-tally-row');
        const id = String(row.item_id ?? ''), key = String(row.item_layer_type_name ?? '');
        const layers = spells.get(id) ?? {};
        if (!(key in layers)) layers[key] = row.item_layer_effect;
        spells.set(id, layers);
    }
    const counts = new Map<string, number[]>();
    const increment = (key: string, level: number): void => {
        const values = counts.get(key) ?? Array<number>(10).fill(0);
        values[level] = (values[level] ?? 0) + 1;
        counts.set(key, values);
    };
    for (const layers of spells.values()) {
        const saved = { item_layer_types: layers };
        const sphere = scalarLayerSlot(layerOf(saved, 'spell_sphere'));
        if (sphere && sphere !== '0') increment('sphere:' + sphere, 0);
        const circle = scalarLayerSlot(layerOf(saved, 'spell_circle'));
        const level = scalarLayerSlot(layerOf(saved, 'spell_level'));
        if (circle && level !== '' && Number.isFinite(Number(level)) && Math.trunc(Number(level)) >= 0 && Math.trunc(Number(level)) <= 9)
            increment('circle:' + circle, Math.trunc(Number(level)));
    }
    return counts;
}
export const definitionFamilyCell = (sort: string | number, segments: CellSegment[], label = String(sort)): TableCell =>
    ({ sort, text: label.toLowerCase(), segments });
export const definitionFieldValue = (row: FromBaseRow, field: DefinitionField): unknown =>
    field.from === 'layer' ? layerOf(row, field.key) : itemsCol(field.from === 'source' ? sourceOf(row) : row, field.key);
const fieldText = (row: FromBaseRow, field: DefinitionField): string => scalarLayerSlot(definitionFieldValue(row, field));
const numeric = (value: string): '' | number => value !== '' && Number.isFinite(Number(value)) ? Number(value) : '';
const firstNumber = (values: (string | number)[]): number | '' => values.map(value => numeric(String(value))).find(value => value !== '') ?? '';
const visibleToViewer = (privacy: unknown, createdBy: unknown): boolean => {
    const viewer = typeof window === 'undefined' ? undefined
        : (window as unknown as { FateTide?: { user?: { id?: string; primaryRole?: string } } }).FateTide?.user;
    return Number(privacy ?? 0) === 0 || ['employee', 'admin', 'super_admin'].includes(viewer?.primaryRole ?? '')
        || (!!viewer?.id && String(createdBy ?? '').toUpperCase() === viewer.id.toUpperCase());
};

/** Unassigned mentas count for every manifestation, as in the saved catalog.
 * Only held selections contribute; reference shells are never tally candidates. */
export function catalogMentaTallies(ids: string[], catalog: Record<string, unknown>, manifestations: string[]): Map<string, number[]> {
    const counts = new Map(manifestations.map(id => [id.toUpperCase(), Array<number>(10).fill(0)]));
    for (const id of ids) {
        const row = catalog[id] ?? catalog[id.toUpperCase()];
        if (!fromBaseRecord(row) || Number(itemsCol(row, 'item_deleted') ?? 0)
            || !visibleToViewer(itemsCol(row, 'item_private'), itemsCol(row, 'item_created_by_id'))) continue;
        const level = Math.trunc(Number(scalarLayerSlot(layerOf(row, 'menta_level'))));
        if (!(level >= 1 && level <= 9)) continue;
        const manifestation = scalarLayerSlot(layerOf(row, 'menta_mental_manifestation')).toUpperCase();
        const targets = manifestation && manifestation !== '0' ? [counts.get(manifestation)] : counts.values();
        for (const values of targets) if (values) values[level] = (values[level] ?? 0) + 1;
    }
    return counts;
}
const fieldPresent = (row: FromBaseRow, field: DefinitionField): boolean => field.from === 'layer'
    ? layerHas(row, field.key) : definitionFieldValue(row, field) != null;
const referenceIds = (row: FromBaseRow, field: DefinitionField): string[] =>
    (field.from === 'layer' ? layerEntriesOf(row, field.key) : [definitionFieldValue(row, field)])
        .map(layerListEntryUuid).filter(Boolean);

export function definitionFamilyFilters(row: FromBaseRow, specs: Record<string, DefinitionFilterSpec>, sourceFallback?: 'na'): NonNullable<TableRow['filters']> {
    return Object.fromEntries(Object.entries(specs).map(([key, spec]) => {
        switch (spec.mode) {
            case 'source': {
                const id = definitionSource(row, sourceFallback).id;
                return [key, id && (sourceFallback || id !== 'D887B8A7-2CED-AB81-3E4E-DDDF4ACD91AE') ? [id] : []];
            }
            case 'number': {
                const value = fieldText(row, spec.field);
                return [key, numeric(value) === '' ? '' : value];
            }
            case 'references': return [key, [...new Set((spec.fields ?? [spec.field]).flatMap(field => referenceIds(row, field)))]];
            case 'boolean': return [key, (spec.presence ? fieldPresent(row, spec.field) : phpJsonTruthy(definitionFieldValue(row, spec.field))) ? '1' : ''];
            case 'flags': return [key, spec.parts.filter(part => fieldPresent(row, part.field)).map(part => part.value)];
        }
    }));
}

/** Related saved rows remain raw and use the same wrapped/lean field reader. */
export function definitionRelatedRows(base: FromBaseRow, table: string, ownerField: string, id: string): FromBaseRow[] {
    const bucket = base[table];
    return bucket && typeof bucket === 'object' ? Object.values(bucket).filter((row): row is FromBaseRow =>
        fromBaseRecord(row) && String(itemsCol(row, ownerField) ?? '').toUpperCase() === id.toUpperCase()) : [];
}

export function definitionFamilyNameCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'name' }> = { kind: 'name' }): TableCell {
    const label = name(row), id = String(itemsCol(row, 'item_id') ?? '');
    if (spec.open_info) {
        const arg = escapeHtml(JSON.stringify(id));
        return definitionFamilyCell(label, [{ kind: 'raw', html:
            `<span class="link-primary" role="button" tabindex="0" style="cursor:pointer;" onclick="openItemInfo(${arg})"`
            + ` onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();openItemInfo(${arg});}">${escapeHtml(label)}</span>`
            + `<a class="small text-muted ms-2 align-baseline text-decoration-none" target="_blank" rel="noopener noreferrer" title="Open item page" href="${link(id)}">↗</a>` }]);
    }
    return definitionFamilyCell(label, [{ kind: 'text', value: label, link: link(id), ...(spec.target ? { target: spec.target } : {}) }]);
}

export function definitionFamilyMediaCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'media' }>, context: DefinitionProjectionContext): TableCell {
    const id = String(itemsCol(row, 'item_id') ?? ''), label = name(row);
    const pictures = Array.isArray(row.pictures) ? { item: compileSurfacePictures(row.pictures, id, 'item', context.cdnUrl) } : row.pictures;
    const visible = (picture: unknown): boolean => visibleToViewer(pictureField0(picture, 'picture_private'), pictureField0(picture, 'picture_created_by_id'));
    const visiblePictures = Object.fromEntries(Object.entries(pictures ?? {}).map(([type, bucket]) =>
        [type, Array.isArray(bucket) ? bucket.filter(visible) : bucket]));
    if (spec.thumbnail) return definitionFamilyCell(label, [{ kind: 'thumbnail', pictures: visiblePictures,
        directUrl: pictureField0(primaryPictureOf({ ...row, pictures: visiblePictures }), 'picture_url') as string | undefined,
        item_id: id, name: label, link: spec.link, w: spec.pixels ?? 80, h: spec.pixels ?? 80 }], spec.searchable ? label : '');
    const picture = spec.icon_only ? null : primaryPictureOf({ ...row, pictures: visiblePictures });
    const url = pictureField0(picture, 'picture_url');
    return definitionFamilyCell(label, [{ kind: 'icon', id, item_type: String(itemsCol(row, 'item_type') ?? ''),
        title: label || false, w: spec.pixels ?? 80, h: spec.pixels ?? 80,
        ...(url ? { pic: String(url) } : {}), ...(spec.link ? { link: link(id), target: '_blank' } : {}) }], spec.searchable ? label : '');
}

export function definitionFamilyScalarCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'scalar' }>): TableCell {
    if (spec.presence && spec.format !== 'boolean' && !fieldPresent(row, spec.field)) return definitionFamilyCell('', []);
    const raw = definitionFieldValue(row, spec.field);
    const values = (spec.entries && spec.field.from === 'layer' ? layerEntriesOf(row, spec.field.key) : [raw])
        .map(value => scalarLayerSlot(value) || (spec.default === undefined ? '' : String(spec.default)));
    if (spec.format === 'boolean') {
        const yes = spec.presence ? fieldPresent(row, spec.field)
            : phpJsonTruthy(typeof raw === 'boolean' ? raw : values[0]);
        const label = yes ? spec.true_text ?? '1' : spec.true_text === undefined ? '0' : '';
        return definitionFamilyCell(spec.sort === 'label' ? label : yes ? 1 : 0,
            yes ? [spec.icon ? { kind: 'icon', id: spec.icon, title: spec.true_text } : text(spec.true_text ?? '✓')] : [],
            spec.searchable === false ? '' : label);
    }
    const labels: string[] = [], segments: CellSegment[] = [];
    for (const value of values) {
        let label = spec.labels ? spec.labels[value] ?? spec.empty ?? '' : plain(value);
        if (spec.singularize_when && numeric(fieldText(row, spec.singularize_when)) === 1) label = label.slice(0, -1);
        const number = numeric(value);
        if (spec.format === 'number') label = number === '' || (spec.nonzero && number === 0) ? ''
            : spec.pretty === false ? value : makeNumberPretty(number);
        if (!label) continue;
        labels.push([spec.label, label].filter(Boolean).join(' '));
        if (spec.icon) segments.push({ kind: 'icon', id: spec.icon, title: spec.label }, text(' '));
        segments.push(spec.zero_icon && number === 0 ? { kind: 'icon', id: spec.zero_icon }
            : spec.badge ? { kind: 'badge', value: label, variant: spec.badge } : text(label));
    }
    const label = labels.join(' ');
    const sort = spec.sort === 'number' || spec.sort === 'first-number' || spec.format === 'number'
        ? spec.nonzero && firstNumber(values) === 0 ? '' : firstNumber(values) : label;
    return definitionFamilyCell(sort, segments, spec.searchable === false ? '' : label);
}

/** Keep the saved first item-layer identity, link order and duplicate link rows.
 * Scanning outgoing catalog values would lose that identity and count semantics. */
function inverseReferences(base: FromBaseRow): Map<string, FromBaseRow[]> {
    const relations = fromBaseRecord(base.definition_relations) ? base.definition_relations : {};
    const types = new Map((Object.values(relations.item_layer_types ?? {}) as FromBaseRow[])
        .map(row => [String(itemsCol(row, 'item_layer_type_id') ?? '').toUpperCase(), String(itemsCol(row, 'item_layer_type_name') ?? '')]));
    const out = new Map<string, FromBaseRow[]>();
    for (const row of Object.values(relations.item_layers ?? {}) as FromBaseRow[]) {
        const type = types.get(String(itemsCol(row, 'item_layer_type_id') ?? '').toUpperCase());
        const target = String(itemsCol(row, 'item_layer_effect') ?? '').toUpperCase();
        const key = `${type}:${target}`;
        if (!type || out.has(key)) continue;
        out.set(key, definitionRelatedRows(relations, 'layers', 'item_layer_id', String(itemsCol(row, 'item_layer_id') ?? '')));
    }
    return out;
}

function definitionReferenceEntries(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'references' }>, context: DefinitionProjectionContext): unknown[] {
    if (spec.reverse) {
        if (!fromBaseRecord(context.base.definition_relations)) throw new FromBaseInputMissing('catalog-definition-relations', spec.field.key);
        return (context.inverseReferences ?? inverseReferences(context.base)).get(`${spec.field.key}:${String(itemsCol(row, 'item_id') ?? '').toUpperCase()}`) ?? [];
    }
    if (spec.nested_types) {
        if (!context.abilityTypes) throw new FromBaseInputMissing('catalog-ability-types', spec.field.key);
        return context.abilityTypes.flatMap(type => layerEntriesOf(row, type).flatMap(entry => {
            const id = layerListEntryUuid(entry);
            const ability = fromBaseRecord(entry) && entry.item_layer_types ? entry : context.catalog[id] ?? context.catalog[id.toUpperCase()];
            return fromBaseRecord(ability) ? layerEntriesOf(ability, spec.field.key) : [];
        }));
    }
    const entries = spec.field.from === 'layer' ? layerEntriesOf(row, spec.field.key) : [definitionFieldValue(row, spec.field)];
    return spec.single ? entries.slice(0, 1) : entries;
}

export function definitionFamilyReferenceCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'references' }>,
    context: DefinitionProjectionContext): TableCell {
    const entries = definitionReferenceEntries(row, spec, context).flatMap(entry => {
        const id = layerListEntryUuid(entry);
        if (!id) return [];
        const source = context.catalog[id] ?? context.catalog[id.toUpperCase()];
        if (spec.reverse && (!source || Number(itemsCol(source, 'item_deleted') ?? 0) !== 0)) return [];
        const original = name(entry) || name(source) || id;
        const label = spec.strip ? original.split(spec.strip).join('') : original;
        return [{ id, label, entry, source }];
    });
    if (spec.order === 'name') entries.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    const shown = spec.limit === undefined ? entries : entries.slice(0, spec.limit);
    const labels: string[] = [], segments: CellSegment[] = [];
    for (const { id, label, entry, source } of shown) {
        if (labels.length && spec.separator) segments.push(text(spec.separator));
        labels.push([spec.label, label].filter(Boolean).join(' '));
        if (spec.quantity) {
            const quantity = scalarLayerSlot(itemsCol(entry, 'qty')) || scalarLayerSlot(entry).split('|')[1] || '';
            if (quantity !== '') segments.push({ kind: 'badge', value: spec.round_quantity ? String(phpRound(Number(quantity))) : quantity, variant: 'dark' });
        }
        const media = fromBaseRecord(source) ? definitionFamilyMediaCell(source, { kind: 'media', pixels: 25 }, context).segments?.[0] : undefined;
        if (spec.badge) {
            const icon = (key: string, title: string, pic?: string): string => iconPh(key, 25, 25, title)
                .replace('<i ', '<i ' + (pic ? `data-pic="${esc(pic)}" ` : ''));
            const content = (spec.prefix_icon ? icon(spec.prefix_icon, spec.label ?? '') + ' ' : '')
                + (spec.display !== 'names' ? icon(id, label, media?.kind === 'icon' ? media.pic : undefined) : '')
                + (spec.display && spec.display !== 'icons' ? esc(label) : '');
            const attrs = spec.open_info ? ` role="button" onclick="${esc(`openItemInfo(${JSON.stringify(id)})`)}" data-ft-info-item-id="${esc(id)}"` : '';
            segments.push({ kind: 'raw', html: `<span class="badge bg-${spec.badge} border m-1"${attrs}>${content}</span>` });
        } else {
            if (spec.prefix_icon) segments.push({ kind: 'icon', id: spec.prefix_icon, title: spec.label });
            if (spec.display !== 'names') segments.push({ kind: 'icon', id, item_type: spec.item_type,
                ...(media?.kind === 'icon' && media.pic ? { pic: media.pic } : {}),
                ...(spec.reverse ? { item_type: String(itemsCol(source, 'item_type') ?? '') } : {}),
                title: label, link: link(id), target: '_blank' });
            if (spec.display && spec.display !== 'icons') segments.push({ kind: 'text', value: (spec.display === 'both' ? ' ' : '') + label, link: link(id), target: '_blank' });
        }
    }
    if (shown.length < entries.length) segments.push({ kind: 'text', value: `+${entries.length - shown.length}`, cls: 'text-muted small' });
    const label = entries.map(entry => [spec.label, entry.label].filter(Boolean).join(' ')).join(spec.separator ?? ' ');
    return definitionFamilyCell(spec.sort === 'count' ? entries.length : spec.sort === 'id' || spec.sort === 'value'
        ? entries.map(entry => entry.id).join(spec.separator ?? ' ') : label, segments,
        spec.searchable === false ? '' : label);
}

function damageCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'damage' }>): TableCell {
    const v = (field: DefinitionField): string => fieldText(row, field);
    const die = v(spec.die), versatile = v(spec.versatile_die), mod = v(spec.mod), type = v(spec.damage_type);
    const label = (die !== '' ? `${v(spec.dice)}d${die}` : '')
        + (versatile !== '' ? ` (${v(spec.versatile_dice)}d${versatile})` : '')
        + ((spec.nonzero_mod ? mod !== '' && Number(mod) !== 0 : phpJsonTruthy(mod)) ? ` + ${mod}` : '');
    return definitionFamilyCell(label, [...(label ? [text(label)] : []),
        ...(type ? [...(label ? [text(' ')] : []), { kind: 'icon' as const, id: type }] : [])]);
}

function definitionSource(row: FromBaseRow, fallback?: 'na'): { id: string; label: string; abbrev: string } {
    // The definition constructor nests ordinary sources and elides N/A. Raw
    // rows retain joined source columns; use the page's reader for either shape.
    const source = sourceOf(row) ?? row;
    let id = String(itemsCol(source, 'source_id') ?? ''), label = String(itemsCol(source, 'source_name') ?? '').trim();
    let abbrev = String(itemsCol(source, 'source_abbreviation') ?? '').trim();
    if (!id && fallback === 'na') { id = 'D887B8A7-2CED-AB81-3E4E-DDDF4ACD91AE'; label = 'N/A'; abbrev = 'NA'; }
    label = plain(label || abbrev); abbrev = plain(abbrev || label);
    return { id, label, abbrev };
}

function sourceCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'source' }>, context: DefinitionProjectionContext): TableCell {
    const rootId = String(itemsCol(row, 'item_id') ?? '');
    const selected = context.catalog[rootId] ?? context.catalog[rootId.toUpperCase()];
    const source = sourceOf(row) ? row : fromBaseRecord(selected) ? selected : row;
    const { id, label, abbrev } = definitionSource(source, spec.fallback);
    const full = label || spec.empty || 'N/A', short = abbrev || spec.empty_abbreviation || 'NA';
    const display = spec.label === 'name' ? full : short;
    return definitionFamilyCell(spec.label ? display : full, [{ kind: 'text', value: display,
        ...(id ? { link: `/source.php?source_id=${encodeURIComponent(id)}` } : {}), tip: full }]);
}

function relatedCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'related' }>, context: DefinitionProjectionContext): TableCell {
    const related = definitionRelatedRows(context.base, spec.table, spec.owner_field, String(itemsCol(row, 'item_id') ?? ''));
    const visible = related.filter(ingredient => visibleToViewer(itemsCol(ingredient, 'item_private'), itemsCol(ingredient, 'item_created_by_id')));
    return definitionFamilyCell(visible.length, visible.map(ingredient => ({ kind: 'icon',
        id: String(itemsCol(ingredient, spec.icon_field) ?? ''), item_type: String(itemsCol(ingredient, 'item_type') ?? ''),
        title: name(ingredient), link: link(String(itemsCol(ingredient, spec.link_field) ?? '')), target: '_blank' })), '');
}

export function projectDefinitionFamilyCell(row: FromBaseRow, spec: DefinitionCellSpec, context: DefinitionProjectionContext): TableCell {
    switch (spec.kind) {
        case 'menta-tally': {
            const values = context.mentaTallies?.get(String(itemsCol(row, 'item_id') ?? '').toUpperCase()) ?? [];
            const count = spec.level === 'all' ? values.reduce((sum, value) => sum + value, 0) : values[spec.level] ?? 0;
            return definitionFamilyCell(count, [text(makeNumberPretty(count))], '');
        }
        case 'spell-tally': {
            const values = context.spellTallies?.get(spec.reference + ':' + String(itemsCol(row, 'item_id') ?? '')) ?? [];
            const count = spec.level === 'all' ? values.reduce((sum, value) => sum + value, 0) : values[spec.level ?? 0] ?? 0;
            return definitionFamilyCell(count, [text(spec.reference === 'sphere' ? String(count) : makeNumberPretty(count))]);
        }
        case 'name': return spec.rune ? definitionNameCell(row) : definitionFamilyNameCell(row, spec);
        case 'pl': return definitionPlCell(plTotal(row), String(itemsCol(row, 'item_id') ?? ''));
        case 'spell': {
            const projected = (context.spell ?? definitionFamilySpellRow(row, !!spec.menta, context)).cells[spec.cell];
            if (!projected) throw new FromBaseStageMissing('catalog-definition-cell', spec.cell);
            return projected;
        }
        case 'media': return definitionFamilyMediaCell(row, spec, context);
        case 'scalar': return definitionFamilyScalarCell(row, spec);
        case 'references': return definitionFamilyReferenceCell(row, spec, context);
        case 'damage': return damageCell(row, spec);
        case 'range': {
            const begin = fieldText(row, spec.begin), end = fieldText(row, spec.end);
            return definitionFamilyCell(phpJsonTruthy(begin) ? numeric(begin) : '',
                phpJsonTruthy(begin) && phpJsonTruthy(end) ? [text(`${begin} (${end})`)] : [], '');
        }
        case 'source': return sourceCell(row, spec, context);
        case 'related': return relatedCell(row, spec, context);
        case 'related-count': {
            const count = definitionRelatedRows(context.base, spec.table, spec.owner_field, String(itemsCol(row, 'item_id') ?? '')).length;
            return definitionFamilyCell(count, [text(String(count))]);
        }
        case 'icon': {
            const value = scalarLayerSlot(definitionFieldValue(row, spec.field));
            const source = context.catalog[value] ?? context.catalog[value.toUpperCase()];
            const label = spec.field.from === 'item' && spec.field.key === 'item_id' ? name(row) : name(source) || plain(value);
            return definitionFamilyCell(label, value ? [{ kind: 'icon', id: spec.icon ?? value,
                item_type: spec.item_type ?? String(itemsCol(row, 'item_type') ?? ''),
                ...(spec.link ? { link: link(value) } : {}) }] : [], '');
        }
        case 'action': {
            const value = scalarLayerSlot(definitionFieldValue(row, spec.field));
            return definitionFamilyCell('', value ? [{ kind: 'icon', id: spec.icon, action: spec.action,
                actionArg: value, variant: spec.variant }] : []);
        }
        case 'parsetext': {
            const value = String(definitionFieldValue(row, spec.field) ?? '');
            return definitionFamilyCell('', value ? [{ kind: 'parsetext', text: value }] : [], plain(value.replace(/<[^>]*>/g, '')).trim());
        }
        case 'recipe': return definitionRecipeCell(row, spec.part, context);
        case 'status': {
            const id = String(itemsCol(row, 'item_id') ?? ''), metadata = plCatalogs().statusEffects.getById(id);
            const type = metadata?.status_type?.toLowerCase() ?? '';
            if (spec.part === 'type') return definitionFamilyCell(type === 'boon' || type === 'bane' ? type : 'neutral',
                type === 'boon' || type === 'bane' ? [{ kind: 'icon', id: type }] : []);
            const variable = metadata?.variable_name ?? '', base = statusQuantityInfo(id)?.base ?? variable.replace(/_\d+$/, '');
            const blocked = getUndispellableStatuses();
            const label = blocked.includes(variable) || blocked.includes(base) || !['boon', 'bane'].includes(type) ? 'undispellable' : '';
            return definitionFamilyCell(label, label ? [{ kind: 'icon', id: label }] : []);
        }
        case 'defense': {
            const kinds = ['resistance', 'immunity', 'vulnerability'].filter(kind => layerEntriesOf(row, 'object_damage_type_' + kind)
                .some(entry => layerListEntryUuid(entry).toUpperCase() === spec.id.toUpperCase()));
            return definitionFamilyCell(kinds.join(' '), kinds.map(kind => ({ kind: 'icon', id: 'object_damage_type_' + kind })));
        }
        case 'lock': return definitionLockCell(row, spec);
        case 'compound': {
            const parts = spec.parts.map(part => projectDefinitionFamilyCell(row, part, context));
            if (spec.require_all && parts.some(cell => !cell.segments?.length)) return definitionFamilyCell('', []);
            const cells = parts.filter(cell => cell.segments?.length), label = cells.map(cell => cell.sort).join(spec.separator ?? ' ');
            return definitionFamilyCell(spec.sort === 'first-number' ? firstNumber(cells.map(cell => cell.sort ?? '')) : label,
                cells.flatMap((cell, index) => [...(index && spec.separator ? [text(spec.separator)] : []), ...cell.segments ?? []]),
                cells.map(cell => cell.text ?? '').join(spec.separator ?? ' '));
        }
        default: throw new Error('catalog-definition-family-projection');
    }
}

function orderValue(row: FromBaseRow, spec: DefinitionOrder, context: DefinitionProjectionContext): string | number {
    if (spec.mode === 'average-damage') {
        const die = numeric(fieldText(row, spec.die));
        if (die === '') return Infinity;
        const rawDice = numeric(fieldText(row, spec.dice)), dice = rawDice === '' ? 1 : rawDice;
        const mod = Number(numeric(fieldText(row, spec.mod)) || 0);
        return dice > 0 && die > 0 ? dice * (die + 1) / 2 + mod : mod;
    }
    const value = fieldText(row, spec.field);
    if (spec.mode === 'shield-last') return value.toUpperCase() === context.shieldArmorType?.toUpperCase() ? 1 : 0;
    if (spec.mode === 'text') return plain(value).toLowerCase();
    const number = numeric(value);
    return number === '' ? spec.missing === 'first' ? -Infinity : Infinity : number;
}

/** Reuse the spell projector; only the authored menta selectors/icons and curse
 * gift cell differ. No second range/duration/roll/component implementation. */
export function definitionFamilySpellRow(row: FromBaseRow, menta: boolean, context: DefinitionProjectionContext): TableRow {
    const projected = definitionRow(row, 'spells', context.catalog);
    projected.cells.gifts = layerHas(row, 'consumes_ability_charges')
        ? definitionFamilyScalarCell(row, { kind: 'scalar', field: { from: 'layer', key: 'consumes_charges_qty' }, format: 'number', default: 1 })
        : definitionFamilyCell('', [text('—')], '');
    if (!menta) return projected;
    const level = scalarLayerSlot(layerOf(row, 'menta_level'));
    const manifestation = scalarLayerSlot(layerOf(row, 'menta_mental_manifestation'));
    projected.cells.level = definitionFamilyScalarCell(row, { kind: 'scalar', field: { from: 'layer', key: 'menta_level' }, format: 'number' });
    projected.cells.manifestation = definitionFamilyCell(manifestation,
        manifestation ? [{ kind: 'icon', id: manifestation, w: 40, h: 40 }] : []);
    projected.cells.requires_subclass = definitionFamilyReferenceCell(row,
        { kind: 'references', field: { from: 'layer', key: 'requires_subclass' }, item_type: 'subclass' }, context);
    const icons: Record<string, string> = { '3ap': 'use_3ap_menta', '2ap': 'use_2ap_menta', '1ap': 'use_1ap_menta', reaction: 'use_r_menta' };
    for (const segment of projected.cells.casting_time!.segments ?? [])
        if (segment.kind === 'icon' && icons[segment.id]) segment.id = icons[segment.id]!;
    if (layerHas(row, 'spell_ritual')) projected.cells.casting_time!.text = 'ritual ' + projected.cells.casting_time!.text;
    if (layerHas(row, 'spell_concentration')) projected.cells.duration!.text = 'concentration ' + projected.cells.duration!.text;
    Object.assign(projected.filters!, { menta_level: level, menta_mental_manifestation: manifestation });
    return projected;
}

type ResolvedDefinition = { definition: FromBaseRow; reasons: string[] };

export const definitionEnchantmentPartEligible = (row: FromBaseRow): boolean =>
    enchantmentPartEligible(row, { itemField: itemsCol, has: layerHas });

function appendDefinitionGaps(cells: Record<string, TableCell>, saved: FromBaseRow, reasons: string[]): void {
    if (!reasons.length) return;
    const identity = cells.name ?? definitionFamilyNameCell(saved);
    identity.segments?.push(text(' unported:' + reasons.join('; ')));
    cells.name = identity;
}

/** Expand all saved template members in the browser. Template UUIDs remain the
 * link/copy identity even when a template contributes several display rows. */
export function definitionEnchantmentRows(ids: string[], base: FromBaseRow, catalog: Record<string, unknown>,
    resolve: (id: string) => ResolvedDefinition): TableRow[] {
    const rows: TableRow[] = [];
    const label = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    for (const id of ids) {
        if (!checkUuid(id)) continue;
        const template = definitionRelatedRows(base, 'enchantment_templates', 'enchantment_template_id', id)[0];
        if (!template) throw new FromBaseInputMissing('enchantment-template', id);
        const members = definitionRelatedRows(base, 'enchantment_template_enchantments', 'enchantment_template_id', id);
        const parts = members.map(member => {
            const memberId = String(itemsCol(member, 'enchantment_item_id') ?? '');
            const saved = catalog[memberId] ?? catalog[memberId.toUpperCase()];
            const type = String(itemsCol(member, 'enchantment_type') ?? '');
            if (!fromBaseRecord(saved)) {
                const parameter = ['item_charges', 'item_rechargeable'].includes(type);
                return { member, definition: {} as FromBaseRow, reasons: parameter ? [] : [`from-base-input-missing:enchantment-member:${memberId}`],
                    display: parameter ? label(type) + ': ' + memberId : plain(itemsCol(template, 'enchantment_template_name')) || memberId };
            }
            return { member, ...resolve(memberId), display: '' };
        });
        // Keep group-wide eligibility; sanctioned generator parts bypass only unenchantable.
        if (parts.some(part => !definitionEnchantmentPartEligible(part.definition))) continue;
        for (const part of parts) {
            if (Number(itemsCol(part.definition, 'item_deleted') ?? 0) !== 0
                || !visibleToViewer(itemsCol(part.definition, 'item_private'), itemsCol(part.definition, 'item_created_by_id'))) continue;
            const display = name(part.definition) || part.display;
            const type = label(String(itemsCol(part.member, 'enchantment_type') ?? ''));
            const labels = layerEntriesOf(part.definition, 'enchantment_item_type').map(entry =>
                label(name(entry) || name(catalog[layerListEntryUuid(entry)]) || scalarLayerSlot(entry))).filter(Boolean);
            if (['enchantment_weapon_type', 'enchantment_weapon_category', 'enchantment_weapon_classification'].some(key => layerHas(part.definition, key))) labels.push('Weapon');
            if (['enchantment_armor_type', 'enchantment_armor_classification'].some(key => layerHas(part.definition, key))) labels.push('Armor');
            const cost = scalarLayerSlot(layerOf(part.definition, 'cost_gp'));
            const cells: Record<string, TableCell> = {
                actions: definitionFamilyCell('', [
                    { kind: 'icon', id: 'modify', w: 18, h: 18,
                        link: '/tools/enchantment_templates/create_enchantment_template.php?from=' + encodeURIComponent(id),
                        title: 'Modify enchantment template', cls: 'btn btn-sm btn-primary d-inline-flex align-items-center justify-content-center' },
                    { kind: 'icon', id: 'copy_enchantment_template', w: 18, h: 18, action: 'copy', actionArg: id, variant: 'primary',
                        title: 'Copy enchantment template UUID', cls: 'btn-sm ms-1 d-inline-flex align-items-center justify-content-center' },
                ]),
                name: definitionFamilyCell(display, [{ kind: 'text', value: display, link: link(id) }],
                    display + ' ' + plain(itemsCol(template, 'enchantment_template_name'))),
                item_type: definitionFamilyCell(type, type ? [text(type)] : []),
                enchantment_item_type: definitionFamilyCell(labels.join(', '), labels.length ? [text(labels.join(', '))] : []),
                cost_gp: definitionFamilyCell(cost === '' ? '' : Number(cost), cost === '' ? []
                    : Number(cost) === 0 ? [{ kind: 'icon', id: 'priceless' }] : [text(makeNumberPretty(cost))], ''),
            };
            appendDefinitionGaps(cells, part.definition, part.reasons);
            rows.push({ item_id: id, cells });
        }
    }
    return rows;
}

function definitionRecipeCell(row: FromBaseRow, part: Extract<DefinitionCellSpec, { kind: 'recipe' }>['part'], context: DefinitionProjectionContext): TableCell {
    const value = (key: string) => scalarLayerSlot(layerOf(row, key));
    if (part === 'skill_tier') {
        const keys = plCostContract.skillTiers.map(tier => tier.requires_skill_tier).filter(key => layerHas(row, key));
        return definitionFamilyCell(keys.length, keys.map(id => ({ kind: 'icon', id })), keys.join(' '));
    }
    if (part === 'check') {
        const dc = value('ability_dc'), contester = value('skill_roll_contester'), segments: CellSegment[] = [];
        if (layerHas(row, 'ability_dc')) segments.push({ kind: 'badge', value: dc, variant: 'dark' });
        if (contester) segments.push({ kind: 'icon', id: contester, item_type: 'skill' });
        return definitionFamilyCell(dc !== '' && Number.isFinite(Number(dc)) ? Number(dc) : '', segments, '');
    }
    if (part === 'time') {
        const count = value('crafting_time_int'), unit = value('crafting_time_type');
        const label = layerHas(row, 'crafting_time_int') && layerHas(row, 'crafting_time_type') ? `${count} ${unit}` : '';
        return definitionFamilyCell(label && count !== '' && Number.isFinite(Number(count)) ? Number(count) : '', label ? [text(label)] : [], '');
    }
    const entry = layerEntriesOf(row, 'fabricate')[0];
    if (entry == null) return definitionFamilyCell('', []);
    let output = inventoryListEntry(entry);
    const token = readLayerScoringToken(entry);
    if (output.qty === null && output.template === null && token?.includes('|')) output = inventoryListEntry(token);
    const id = output.uuid;
    if (!id) return definitionFamilyCell('', []);
    const nested = fromBaseRecord(entry) && name(entry) ? entry : context.catalog[id] ?? context.catalog[id.toUpperCase()];
    const label = name(nested), segments: CellSegment[] = [];
    if (fromBaseRecord(nested)) {
        const media = definitionFamilyMediaCell(nested, { kind: 'media', pixels: 40, link: true }, context).segments?.[0];
        if (media?.kind === 'icon' && media.pic) segments.push({ ...media, cls: 'me-2' });
    }
    if (output.qty !== null) segments.push({ kind: 'badge', value: String(Math.round(output.qty)), variant: 'dark' });
    segments.push({ kind: 'text', value: label, link: link(id), target: '_blank' });
    return definitionFamilyCell(label, segments);
}

function definitionLockCell(row: FromBaseRow, spec: Extract<DefinitionCellSpec, { kind: 'lock' }>): TableCell {
    if (!layerHas(row, 'lockpick_difficulty') && !layerHas(row, 'dice')) return definitionFamilyCell('', []);
    const dice = resolveDiceCount(scalarLayerSlot(layerOf(row, 'lockpick_difficulty')), scalarLayerSlot(layerOf(row, 'dice')));
    const arc = ARC_MAX - ARC_MIN;
    const number = spec.stat === 'sweet_spot' ? Math.min(arc, baseSweetSpotWidth(dice) + spec.tier * 10)
        : spec.stat === 'feel' ? feelFalloffForTier(dice, spec.tier) : struggleSecondsForTier(dice, spec.tier);
    const pretty = number.toFixed(spec.stat === 'strain' ? 2 : 1).replace(/\.?0+$/, '');
    const label = spec.stat === 'sweet_spot' ? `${pretty}° (${(100 * number / arc).toFixed(1)}%)`
        : pretty + (spec.stat === 'feel' ? '°' : 's');
    return definitionFamilyCell(number, [text(label)]);
}

function readsDefinitionLayers(spec: DefinitionCellSpec): boolean {
    switch (spec.kind) {
        case 'compound': return spec.parts.some(readsDefinitionLayers);
        case 'damage': return [spec.dice, spec.die, spec.versatile_dice, spec.versatile_die, spec.mod, spec.damage_type]
            .some(field => field.from === 'layer');
        case 'range': return [spec.begin, spec.end].some(field => field.from === 'layer');
        case 'scalar': return spec.field.from === 'layer' || spec.singularize_when?.from === 'layer';
        case 'references': return spec.field.from === 'layer' || !!spec.nested_types;
        case 'name': return !!spec.rune;
        case 'pl': case 'spell': case 'enchantment': case 'recipe': case 'defense': case 'lock': return true;
        default: return 'field' in spec && spec.field.from === 'layer';
    }
}

function filterReadsDefinitionLayers(spec: DefinitionFilterSpec): boolean {
    switch (spec.mode) {
        case 'source': return false;
        case 'references': return (spec.fields ?? [spec.field]).some(field => field.from === 'layer');
        case 'flags': return spec.parts.some(part => part.field.from === 'layer');
        default: return spec.field.from === 'layer';
    }
}

// Saved families read authored fields and use the existing browser calculations directly.
// Families with inherited classification/enchantment values retain the full finalizer below.
const SAVED_SHIELD_ARMOR_TYPE = String(Object.values(FROM_BASE_STATIC_REGISTRIES.armor_type ?? {}).find(row => row?.variable_name === 'shield')?.item_id ?? '');
const SAVED_DEFINITION_KINDS = new Set<DefinitionCellSpec['kind']>(['name', 'media', 'scalar', 'references', 'source', 'compound', 'spell-tally', 'menta-tally', 'icon', 'parsetext', 'status', 'damage', 'range', 'related', 'recipe', 'defense', 'lock', 'action']);
function assertSavedDefinitionSpec(spec: DefinitionCellSpec): void {
    if (!SAVED_DEFINITION_KINDS.has(spec.kind) || (spec.kind === 'references' && spec.nested_types))
        throw new Error('catalog-definition-family-saved-unsupported:' + spec.kind);
    if (spec.kind === 'compound') spec.parts.forEach(assertSavedDefinitionSpec);
}

/** Saved-field tables avoid rule finalization. Layer cells, filters and ordering
 * share one finalizer before render-table consumes the projected rows. */
export function hydrateDefinitionFamilyCells(rows: TableRow[], input: unknown, metadata?: FromBaseEntryMetadata): boolean {
    if (!fromBaseRecord(input) || !('schema' in input)) return false;
    if (input.schema !== DEFINITION_FAMILY_SCHEMA) throw new Error('catalog-definition-family-schema');
    const graph = input as unknown as DefinitionFamilyGraph;
    if (!Array.isArray(graph.definition_ids) || !Array.isArray(graph.columns) || rows.length !== graph.definition_ids.length
        || rows.some((row, index) => row.item_id !== graph.definition_ids[index])) throw new Error('catalog-definition-family-selection');
    if (!rows.length) return true;
    const savedDefinitions = graph.options?.saved_definitions === true;
    if (savedDefinitions) {
        if (graph.family === 'enchantment_templates') throw new Error('catalog-definition-family-saved-unsupported:enchantment_templates');
        for (const column of graph.columns)
            [column.project, ...Object.values(column.filter_projects ?? {})].forEach(assertSavedDefinitionSpec);
    }
    assertUncomputedFromBase(graph.base);
    if (!savedDefinitions) assertUncomputedFromBase(graph.shared);
    const base = savedDefinitions ? graph.base : inventoryCatalogScope(graph.shared!, graph.base).base;
    const compiler = savedDefinitions ? undefined : metadata ?? entityRuntimeMetadata(graph.shared!.metadata as Parameters<typeof entityRuntimeMetadata>[0]);
    const inventory = savedDefinitions ? undefined : mergeInventoryMetadata(compiler!.inventory, base.inventory_metadata);
    const needsFinalization = !savedDefinitions && (graph.options?.finalize_definitions || graph.family === 'enchantment_templates'
        || graph.columns.some(column => readsDefinitionLayers(column.project)
            || Object.values(column.filter_projects ?? {}).some(readsDefinitionLayers))
        || Object.values(graph.options?.filters ?? {}).some(filterReadsDefinitionLayers)
        || (graph.options?.order ?? []).some(spec => (spec.mode === 'average-damage'
            ? [spec.dice, spec.die, spec.mod] : [spec.field]).some(field => field.from === 'layer')));
    const finalize = needsFinalization ? createNpcDefinitionFinalizer(base, compiler!.player, inventory!) : null;
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const inverse = inverseReferences(base);
    const spellTallies = graph.options?.spell_tally ? catalogSpellTallies(base.spell_tally_rows) : undefined;
    const mentaTallies = graph.options?.menta_tally
        ? catalogMentaTallies(base.menta_tally_ids as string[], catalog, graph.definition_ids) : undefined;
    const order = new Map<TableRow, (string | number)[]>();
    const resolved = new Map<string, ResolvedDefinition>();
    const resolve = (id: string): ResolvedDefinition => {
        const prior = resolved.get(id.toUpperCase());
        if (prior) return prior;
        const saved = catalog[id] ?? catalog[id.toUpperCase()];
        if (!fromBaseRecord(saved)) throw new FromBaseInputMissing('root-definition', id);
        let definition = saved, reasons: string[] = [];
        if (finalize) try {
            const compiled = finalize.definition(id, []);
            if (!compiled) throw new FromBaseInputMissing('root-definition', id);
            definition = compiled;
            reasons = fromBaseCatalogFinalizationGaps(base, inventory!, compiled);
        } catch (error) {
            if (!(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
            reasons = [error.message];
        }
        const result = { definition, reasons };
        resolved.set(id.toUpperCase(), result);
        return result;
    };
    const spellSpec = graph.columns.find(column => column.project.kind === 'spell')?.project;
    const sourceFallback = graph.columns.some(column => column.project.kind === 'source' && column.project.fallback === 'na') ? 'na' : undefined;
    if (graph.family === 'enchantment_templates') {
        rows.splice(0, rows.length, ...definitionEnchantmentRows(graph.definition_ids, base, catalog, resolve));
    } else for (const row of rows) {
        const { definition, reasons } = resolve(row.item_id);
        const context: DefinitionProjectionContext = { catalog, base, cdnUrl: savedDefinitions
            ? (typeof window === 'undefined' ? undefined : (window as unknown as { CDN_URL?: string }).CDN_URL) : inventory!.cdnUrl,
            shieldArmorType: compiler?.player.equippedCounts.shieldArmorType ?? SAVED_SHIELD_ARMOR_TYPE,
            abilityTypes: inventory?.abilityTypes, inverseReferences: inverse, spellTallies, mentaTallies,
            ...(spellSpec?.kind === 'spell' ? { spell: definitionFamilySpellRow(definition, !!spellSpec.menta, { catalog, base }) } : {}) };
        row.cells = Object.fromEntries(graph.columns.map(column => [column.key, projectDefinitionFamilyCell(definition, column.project, context)]));
        if (context.spell) row.filters = context.spell.filters;
        else if (graph.columns.some(column => column.project.kind === 'pl')) row.filters = { pl: plTotal(definition)?.toString() ?? '' };
        if (graph.options?.filters) row.filters = definitionFamilyFilters(definition, graph.options.filters, sourceFallback);
        for (const column of graph.columns) for (const [key, spec] of Object.entries(column.filter_projects ?? {})) {
            row.filters ??= {};
            row.filters[key] = spec.kind === 'references'
                ? [...new Set(definitionReferenceEntries(definition, spec, context).map(layerListEntryUuid).filter(Boolean))]
                : String(projectDefinitionFamilyCell(definition, spec, context).sort ?? '');
        }
        if (graph.options?.order) order.set(row, graph.options.order.map(spec => orderValue(definition, spec, context)));
        appendDefinitionGaps(row.cells, definition, reasons);
    }
    if (graph.options?.order?.length) rows.sort((a, b) => {
        const av = order.get(a)!, bv = order.get(b)!;
        for (let index = 0; index < av.length; index++) {
            const left = av[index]!, right = bv[index]!;
            const comparison = left === right ? 0 : typeof left === 'number' && typeof right === 'number'
                ? left < right ? -1 : 1 : String(left).localeCompare(String(right));
            if (comparison) return comparison;
        }
        return 0;
    });
    else rows.sort((a, b) => String(a.cells.name?.sort ?? '').localeCompare(String(b.cells.name?.sort ?? ''), undefined, { sensitivity: 'base' }));
    return true;
}
